// Status widgets shared by the dashboard and the station's Sendung page.

import { useState } from "react";
import { api, fmtMs, fmtTime, since, toast, useLoad } from "../api";
import { Empty, Meter, StateBadge, useMe } from "../ui";

export function OnAirCard({ engine }: { engine: any }) {
  const np = engine?.now_playing;
  const live = engine?.on_air?.kind === "stream";
  const start = np?.item?.marks?.cue_in ?? 0;
  const pos = np ? Math.max(0, np.pos_ms - start) : 0;
  const pct = np && np.len_ms ? Math.min(100, (pos / np.len_ms) * 100) : 0;
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 style={{ margin: 0 }}>On Air</h2>
        <span className="spacer" />
        {live ? <span className="badge live">LIVE · {engine.on_air.name}</span> : engine?.state === "läuft" ? <span className="badge ok">AUTOMATION</span> : <span className="badge bad">OFF AIR</span>}
      </div>
      {live ? (
        <div className="onair">
          <div className="cover">●</div>
          <div>
            <div className="t">{engine.on_air.name}</div>
            <div className="muted">Stream hat übernommen. Automation läuft {engine.metadata ? `· Metadaten: ${engine.metadata}` : ""}</div>
          </div>
        </div>
      ) : np ? (
        <div className="onair">
          <div className="cover">{(np.item.artist || np.item.title || "?").slice(0, 1).toUpperCase()}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="t">{np.item.title}</div>
            <div className="muted">
              {np.item.artist}
              {np.item.category ? ` · ${np.item.category}` : ""}
            </div>
            <div className="row small muted" style={{ marginTop: 6 }}>
              <span>{fmtMs(pos)}</span>
              <div className="progress" style={{ flex: 1 }}><div style={{ width: `${pct}%` }} /></div>
              <span>−{fmtMs(Math.max(0, np.len_ms - pos))}</span>
            </div>
          </div>
        </div>
      ) : (
        <Empty>{engine?.state === "läuft" ? "Nichts in der Wiedergabe – die Engine lädt den nächsten Titel." : `Engine ${engine?.state ?? "unbekannt"}${engine?.error ? `: ${engine.error}` : ""}`}</Empty>
      )}
      {engine?.upcoming?.length > 0 && (
        <div style={{ marginTop: 12 }}>
          <div className="small muted" style={{ marginBottom: 4 }}>Als Nächstes</div>
          {engine.upcoming.slice(0, 3).map((u: any) => (
            <div key={u.id} className="small">{u.artist ? `${u.artist} – ` : ""}{u.title}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export function OutputsCard({ engine }: { engine: any }) {
  const outs: any[] = engine?.outputs ?? [];
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Ausgänge</h2>
        <span className="spacer" />
        <a className="small" href="#/einstellungen/ausgaenge">Einstellungen</a>
      </div>
      {outs.length === 0 ? (
        <Empty>Kein Ausgang eingerichtet. <a href="#/einstellungen/ausgaenge">Ausgang anlegen</a></Empty>
      ) : (
        <table className="list">
          <tbody>
            {outs.map((o) => (
              <tr key={o.id}>
                <td><b>{o.name}</b>{o.error && <div className="small" style={{ color: "var(--bad)" }}>{o.error}</div>}</td>
                <td><StateBadge state={o.state} /></td>
                <td className="small muted">{o.state === "verbunden" ? since(o.since) : ""}</td>
                <td className="small muted">{o.bytes ? `${(o.bytes / 1048576).toFixed(1)} MB` : ""}{o.reconnects ? ` · ${o.reconnects}× neu verbunden` : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function MonitorsCard({ engine }: { engine: any }) {
  const mons: any[] = engine?.monitors ?? [];
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Stream-Monitor</h2>
        <span className="spacer" />
        <a className="small" href="#/einstellungen/monitor">Einstellungen</a>
      </div>
      {mons.length === 0 ? (
        <Empty>Kein Stream wird überwacht. <a href="#/einstellungen/monitor">Stream eintragen</a></Empty>
      ) : (
        <table className="list">
          <tbody>
            {mons.map((m) => (
              <tr key={m.id}>
                <td><b>{m.name}</b> {m.on_air && <span className="badge live">ON AIR</span>}{m.error && m.state !== "verbunden" && <div className="small muted">{m.error}</div>}</td>
                <td><StateBadge state={m.state} /></td>
                <td style={{ width: 160 }}><Meter db={m.level_db} /></td>
                <td className="small muted">{m.sending ? "sendet" : m.state === "verbunden" ? "zu leise" : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function ControlCard({ engine }: { engine: any }) {
  const me = useMe();
  const [busy, setBusy] = useState(false);
  const run = async (path: string, ok: string, confirmText?: string) => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try {
      await api(`/api/engine/${path}`, { method: "POST" });
      toast(ok);
    } catch (e: any) {
      toast(e.message, true);
    } finally {
      setBusy(false);
    }
  };
  const p = engine?.processor;
  const s = engine?.silence;
  return (
    <div className="card">
      <h2>Engine</h2>
      <div className="grid g2 small" style={{ marginBottom: 12 }}>
        <div>Zustand: <StateBadge state={engine?.state} /></div>
        <div>Version: {engine?.version ?? "–"}</div>
        <div>Läuft {since(engine?.started_at)}</div>
        <div>Einstellungen aus: {engine?.config_source === "cache" ? <span className="badge warn">Zwischenspeicher</span> : engine?.config_source ?? "–"}</div>
        <div>Klang: {p ? (p.enabled ? `${p.mode} · ${p.state}${p.preset ? ` · ${p.preset}` : ""}` : "StereoTool aus (Sicherheitsbegrenzer)") : "–"}</div>
        <div>Stille: {s?.silent ? <span className="badge bad">STILLE</span> : "nein"} · Pegel</div>
      </div>
      {s && <Meter db={s.level_db} mark={s.threshold_db} />}
      <div className="row wrap" style={{ marginTop: 14 }}>
        <button className="btn" disabled={busy} onClick={() => run("skip", "Übersprungen")}>⏭ Überspringen</button>
        {me?.role === "admin" && (
          <>
            <button className="btn" disabled={busy} onClick={() => run("reload", "Plan und Einstellungen neu geladen")} title="Plan und Einstellungen neu laden; Hörer bleiben verbunden">↻ Sanfter Neustart</button>
            <button className="btn danger" disabled={busy}
              onClick={() => run("restart", "Engine startet neu", "Harter Neustart: Engine und Ausgänge starten neu. Hörer auf dem Test-Mount hören kurz die Pausenschleife. Fortfahren?")}
              title="Engine-Prozess neu starten; Hörer hören kurz die Pausenschleife">⟲ Harter Neustart</button>
          </>
        )}
      </div>
    </div>
  );
}

const EVENT_LABEL: Record<string, string> = {
  engine_start: "Engine gestartet",
  engine_restart: "Harter Neustart",
  reload: "Sanfter Neustart",
  takeover: "Übernahme",
  return: "Zurück zur Automation",
  silence: "Stille erkannt",
  silence_end: "Stille beendet",
  fallback: "Ausweichtitel",
  skipped: "Titel übersprungen (Fehler)",
  skip: "Übersprungen",
  output_connected: "Ausgang verbunden",
  output_down: "Ausgang seit 1 min nicht verbunden",
  output_error: "Ausgang gestört",
  preset: "Preset gewechselt",
  operator_reload: "Sanfter Neustart (Bedienung)",
  operator_restart: "Harter Neustart (Bedienung)",
  operator_skip: "Überspringen (Bedienung)",
  reload_failed: "Neu laden fehlgeschlagen",
  next_failed: "Nächster Titel: Fehler (Rückfall aktiv)",
  rotation_relaxed: "Rotation: Regel gelockert",
  overlay: "Overlay",
};

export function eventText(e: any): string {
  const d = e.detail ?? {};
  const label = EVENT_LABEL[e.type] ?? e.type.replace(/_/g, " ");
  const extra = d.name ?? d.title ?? d.error ?? d.reason ?? (d.seconds ? `${d.seconds} s` : "");
  return extra ? `${label}: ${extra}` : label;
}

export function EventsCard({ limit = 12 }: { limit?: number }) {
  const { data, reload } = useLoad<any[]>(`/api/events?limit=${limit}`);
  return (
    <div className="card">
      <div className="row" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>Ereignisse</h2>
        <span className="spacer" />
        <button className="btn small" onClick={reload}>Aktualisieren</button>
      </div>
      {!data?.length ? <Empty>Noch keine Ereignisse.</Empty> : (
        <table className="list">
          <tbody>
            {data.map((e) => (
              <tr key={e.id}>
                <td className="small muted" style={{ width: 120 }}>{fmtTime(e.at)}</td>
                <td>{eventText(e)}</td>
                <td className="small muted">{e.actor && e.actor !== "engine" ? e.actor : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export function RecentCard({ recent }: { recent: any[] | undefined }) {
  return (
    <div className="card">
      <h2>Zuletzt gespielt</h2>
      {!recent?.length ? <Empty>Noch nichts gesendet.</Empty> : (
        <table className="list">
          <tbody>
            {recent.map((r, i) => (
              <tr key={i}>
                <td className="small muted" style={{ width: 80 }}>{fmtTime(r.started_at)}</td>
                <td>{r.artist ? `${r.artist} – ` : ""}{r.title}</td>
                <td className="small muted">{r.category}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** Plays the engine the log or the rotation – and why (Dashboard, Log). */
export function PlaysBanner({ health }: { health: any }) {
  if (!health?.plays) return null;
  if (health.plays === "log") return <div className="banner ok">▶ Die Engine spielt den <b style={{ margin: "0 4px" }}>Sendeplan</b> (Uhren und Wochenraster).</div>;
  return (
    <div className="banner warn">
      ⚠ <span>Die Engine spielt gerade die <b>Rotation</b>, nicht den Sendeplan. {health.plays_reason}</span>
      <span className="spacer" /><a href="#/plan/sendeplan">Zum Sendeplan →</a>
    </div>
  );
}
